import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { opportunityService } from '../services/api';

function formatUSD(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + Number(n).toLocaleString();
}

export default function ManagerApprovals() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadOpportunities();
  }, []);

  const loadOpportunities = () => {
    setLoading(true);
    opportunityService.getAll()
      .then(data => {
        // Filter to show only pending_partner_approval
        const pending = (data.opportunities || []).filter(o => o.status === 'pending_partner_approval');
        setOpportunities(pending);
      })
      .catch(() => setMessage({ type: 'error', text: 'Failed to load pending approvals' }))
      .finally(() => setLoading(false));
  };

  const handleApprove = async (opp) => {
    if (!window.confirm(`Approve "${opp.opportunity_name}" and submit to Cloud Kinetics?`)) return;

    setProcessing(prev => ({ ...prev, [opp.opportunity_id]: 'approving' }));
    try {
      await opportunityService.partnerApprove(opp.opportunity_id);
      setMessage({ type: 'success', text: `${opp.opportunity_name} approved and submitted to CK` });
      loadOpportunities(); // Reload to remove from list
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to approve' });
    } finally {
      setProcessing(prev => ({ ...prev, [opp.opportunity_id]: null }));
    }
  };

  const handleReject = async (opp) => {
    const reason = window.prompt('Rejection reason (will be visible to salesperson):');
    if (!reason) return;

    setProcessing(prev => ({ ...prev, [opp.opportunity_id]: 'rejecting' }));
    try {
      await opportunityService.partnerReject(opp.opportunity_id, reason);
      setMessage({ type: 'success', text: `${opp.opportunity_name} rejected and returned to salesperson` });
      loadOpportunities();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to reject' });
    } finally {
      setProcessing(prev => ({ ...prev, [opp.opportunity_id]: null }));
    }
  };

  if (loading) return <div className="loading">Loading pending approvals…</div>;

  return (
    <div className="manager-approvals">
      <div className="approvals-header">
        <h2>Pending Approvals</h2>
        <span className="approvals-count">{opportunities.length} awaiting your review</span>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {opportunities.length === 0 ? (
        <div className="no-data">No pending approvals</div>
      ) : (
        <div className="approvals-list">
          {opportunities.map(opp => (
            <div key={opp.opportunity_id} className="approval-card">
              <div className="approval-card-header">
                <div>
                  <Link to={`/opportunities/${opp.opportunity_id}`} className="approval-card-title">
                    {opp.opportunity_name}
                  </Link>
                  {opp.cpn_id && <span className="approval-cpn-id">{opp.cpn_id}</span>}
                </div>
                <div className="approval-card-meta">
                  <span className="approval-submitted-by">
                    By: {opp.created_by}
                  </span>
                  <span className="approval-date">
                    {new Date(opp.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="approval-card-body">
                <div className="approval-detail">
                  <strong>Company:</strong> {opp.company_name}
                </div>
                <div className="approval-detail">
                  <strong>Contact:</strong> {opp.contact_first_name} {opp.contact_last_name} ({opp.contact_email})
                </div>
                <div className="approval-detail">
                  <strong>ARR:</strong> {formatUSD(opp.arr_usd)}
                </div>
                <div className="approval-detail">
                  <strong>Deal Type:</strong> {opp.deal_type}
                </div>
                <div className="approval-detail">
                  <strong>Close Date:</strong> {opp.estimated_close_date}
                </div>
              </div>

              <div className="approval-card-actions">
                <button
                  className="btn btn-success"
                  onClick={() => handleApprove(opp)}
                  disabled={processing[opp.opportunity_id]}
                >
                  {processing[opp.opportunity_id] === 'approving' ? 'Approving…' : 'Approve & Submit'}
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleReject(opp)}
                  disabled={processing[opp.opportunity_id]}
                >
                  {processing[opp.opportunity_id] === 'rejecting' ? 'Rejecting…' : 'Reject'}
                </button>
                <Link to={`/opportunities/${opp.opportunity_id}`} className="btn btn-secondary">
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
